import React, { FC, ReactElement } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { body2 } from '@westwing/ui-kit/typography';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import { StyledArrowIcon, Direction } from '@westwing/ui-kit/Icons/StyledIcons';
import SvgArrow from '@westwing/ui-kit/Icons/Svgs/ArrowIcon';
import StyledListItem from '../../Generic/List/StyledListItem';
import HybridLink from '../../Generic/Links/HybridLink';
import WideFlexContainer from '../../Generic/Containers';
import { useOverlayUrlService } from '../useOverlay';
import useBellaPush from '../../../hooks/useBellaHistoryPush';
import { MenuItemInterface, MenuItemType } from './types';
import { AppShellLoginState, SessionTypes } from 'Client/redux/user/types';
import { setRedirectUrl } from 'Client/redux/user/actions';
import { remoteLoginEvent } from 'Common/events/service';
import { BellaTestIds, VictorQaClasses } from 'Common/qaIds';
import StateInterface from 'Client/redux/types';
import useLoginAndRegisterOverlayWithTracking from 'Client/hooks/useLoginAndRegisterOverlayWithTracking';

const StyledMenuLink = styled(HybridLink)`
    text-decoration: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: ${({ theme }) => theme.colors.charcoal100};
    ${forDesktop`
          :hover {
              text-decoration: underline;
          }
    `}
`;

const StyledMenuBtn = styled(WideFlexContainer)`
    cursor: pointer;
`;

const StyledSpan = styled.span<{ highlighted: boolean }>`
    ${body2}
    ${({ highlighted, theme }) => (highlighted ? `color: ${theme.colors.turquoise100};` : '')}
`;

const ArrowIcon = styled(StyledArrowIcon)`
    display: block;
    ${forDesktop`
        display: none;
    `}
`;

export type MenuItemProps = MenuItemInterface & {
    closeMenuFn?: () => void;
    trackItemClick?: (name: string) => void;
    onClick?: (event: React.MouseEvent) => void;
};

const ExternalLinkItem: FC<{
    hasArrow: boolean;
    title: string;
    url: string;
    highlighted: boolean;
    onClick: ((event: React.MouseEvent) => void) | undefined;
    className?: string;
}> = ({ hasArrow, title, url, onClick, highlighted, className }) => (
    <StyledMenuLink to={url} onClick={onClick} className={className}>
        <StyledSpan highlighted={highlighted}>{title}</StyledSpan>
        {hasArrow && <ArrowIcon direction={Direction.Right} />}
    </StyledMenuLink>
);

const StyledIcon = styled.span`
    width: 7px;
    height: 14px;
`;

// eslint-disable-next-line complexity
const MenuItem: FC<MenuItemProps> = ({
    title,
    url,
    itemType,
    onClick,
    trackItemClick = () => {},
    closeMenuFn = () => {},
    highlighted = false,
    hexColor,
    fullLoginRequired = false,
    className,
    testId,
}): ReactElement => {
    const dispatch = useDispatch();
    const { showLoginAndRegisterOverlay, trackLoginOverlayShow } = useLoginAndRegisterOverlayWithTracking();
    const push = useBellaPush();
    const { data, isLoggedIn } = useSelector<StateInterface, AppShellLoginState>(state => state.appShellLogin);
    const isUserLoggedIn = isLoggedIn && data?.sessionType === SessionTypes.full;
    const email = data?.email;

    const isExternal = itemType === MenuItemType.ExternalLink || itemType === MenuItemType.ExternalLinkArrowed;

    const forceLogin = !isUserLoggedIn && fullLoginRequired;

    const { hasAnActiveOverlay } = useOverlayUrlService();

    const remoteLogin = remoteLoginEvent();
    const closeMenu = () => {
        trackItemClick(title);
        closeMenuFn();
    };

    const onClickFunction = (event: React.MouseEvent) => {
        trackItemClick(title);
        return onClick && onClick(event);
    };

    const handleForceLogin = () => {
        closeMenu();
        dispatch(setRedirectUrl(url));
        showLoginAndRegisterOverlay();
        trackLoginOverlayShow();
        remoteLogin({ redirectUrl: url, email });
    };

    const showMenuItemAsButton = itemType === MenuItemType.Button || forceLogin;
    const showMenuItemAsInternalLink = itemType === MenuItemType.InternalLink && !forceLogin;
    const showMenuItemAsExternalLink = isExternal && !forceLogin;

    const handleMenuLinkClick = () => {
        push(url, hasAnActiveOverlay);
        if (hasAnActiveOverlay) {
            trackItemClick(title);
            return;
        }
        closeMenu();
    };

    return (
        <StyledListItem data-testid={testId || BellaTestIds.menuItem} hexColor={hexColor}>
            {showMenuItemAsExternalLink && (
                <ExternalLinkItem
                    hasArrow={itemType === MenuItemType.ExternalLinkArrowed}
                    title={title}
                    url={url}
                    onClick={() => trackItemClick(title)}
                    highlighted={highlighted}
                    className={className}
                />
            )}
            {showMenuItemAsInternalLink && (
                <StyledMenuBtn
                    data-testid={BellaTestIds.burgerMenuNavitem}
                    onClick={handleMenuLinkClick}
                    className={className}
                >
                    <StyledSpan highlighted={highlighted}>{title}</StyledSpan>
                </StyledMenuBtn>
            )}
            {showMenuItemAsButton && (
                <StyledMenuBtn
                    data-testid={BellaTestIds.menuNavitem}
                    onClick={forceLogin ? handleForceLogin : onClickFunction}
                    className={`${className} ${VictorQaClasses.headerMenuLink}`}
                >
                    <StyledSpan highlighted={highlighted}>{title}</StyledSpan>
                    <StyledIcon>
                        <SvgArrow />
                    </StyledIcon>
                </StyledMenuBtn>
            )}
        </StyledListItem>
    );
};

export default MenuItem;
